import { BCBarcode, IBarcodeProps } from '@bimser/components';
import { FormItemTypes, getEnumTypeKeys, IDictionary } from '@bimser/core';
import { JSX } from 'react';
import { IPropertyField } from '../../../../entities/common';
import EBarcodeTypes from '../../../../entities/EBarcodeTypes';
import EBarcodeValueTypes from '../../../../entities/EBarcodeValueTypes';
import InputControl from '../../base/InputControl';
import { filterFields } from '../../helper';
import { IBarcodeProperties } from './entities/IBarcodeProperties';
import _get from 'lodash/get';
import { IPropertyChangedEventArgs } from '../../entities/EventArgs';

export default class Barcode extends InputControl<string> {
	codeType: EBarcodeTypes;
	valueType: EBarcodeValueTypes;
	fieldName: string;

	__oldSubscription: () => void;

	constructor() {
		super();

		this.__beforeFieldNameProperty = this.__beforeFieldNameProperty.bind(this);
		this.__subscribeFieldName = this.__subscribeFieldName.bind(this);

		this.controlType = FormItemTypes.EControlTypes.Barcode;
		this.valueType = EBarcodeValueTypes.Constant;
		this.codeType = EBarcodeTypes.QR;
		this.value = 'Barcode Text';
		this.fieldName = null;
	}

	__loadProperties(data: IBarcodeProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.codeType = data.codeType;
		this.__proxyChanged.valueType = data.valueType;

		this.__beforeFieldNameProperty(data);
	}

	__beforeFieldNameProperty(data: IBarcodeProperties) {
		const subscribeFieldName = this.fieldName !== data.fieldName;

		this.__proxyChanged.fieldName = data.fieldName;

		if (subscribeFieldName) {
			this.__subscribeFieldName();
		}
	}

	__subscribeFieldName(): void {
		this.__oldSubscription?.();

		if (this.fieldName) {
			const [controlId, ...propertyPath] = this.fieldName.split('.');

			this.__oldSubscription = this.__form.controlMap[controlId].subscribe(
				'event:OnPropertyChanged',
				(args: IPropertyChangedEventArgs<any>) => {
					if (propertyPath.join('.') === args.name) {
						this.setValue(args.newValue);
					}
				}
			);
		}
	}

	__getPropertyValue(path: string): string {
		if (!path) {
			return this.value;
		}

		let findValue = _get(this.__form, path.split('.'));

		if (findValue) {
			if (typeof findValue === 'number') {
				findValue = findValue.toString();
			}

			if (findValue !== this.value) {
				this.setValue(findValue);
			}
		}

		return findValue;
	}

	__getValue() {
		if (this.valueType === EBarcodeValueTypes.Constant) {
			return this.value;
		}

		return this.__getPropertyValue(this.fieldName);
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...filterFields(super.__getPropertyFields(), ['readOnly']),
			{
				name: 'value',
				type: 'string',
				common: true
			},
			{
				name: 'codeType',
				type: 'enum',
				enumMembers: getEnumTypeKeys(EBarcodeTypes).map(
					type => type as keyof typeof EBarcodeTypes
				)
			},
			{
				name: 'valueType',
				type: 'enum',
				enumMembers: getEnumTypeKeys(EBarcodeValueTypes).map(
					type => type as keyof typeof EBarcodeValueTypes
				)
			}
		];
	}

	__getComponentData(): IBarcodeProps & IDictionary<any> {
		return {
			...super.__getComponentData(),
			codeType: this.codeType,
			valueType: this.valueType,
			fieldName: this.fieldName,
			value: this.__getValue()
		};
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCBarcode
				value={this.__getValue()}
				codeType={this.codeType}
				style={this.style}
			/>
		);
	}
}
